import { FormattedMessage, useIntl } from 'react-intl'
import {
  Button,
  FormControlLabel,
  Checkbox,
  Paper,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  FormHelperText,
  SelectChangeEvent
} from '@mui/material'
import { InputField } from '../Common/InputField'
import { IContactUsForm, IContactUsFormProps } from '../../interface'
import { US, allCountries } from 'country-region-data'
import { FormProvider, RegisterOptions, useController, useForm, UseFormSetValue } from 'react-hook-form'
import { useEffect } from 'react'
import { validateEmail, validatePhone, validateRequired } from 'src/validators/validate'
import Recaptcha from '../Common/Recaptcha'
import { IntlLink } from '../Common/IntlComponents'

const usStatesList = US[2]

/**
 * Contact Us form
 * @param onSubmit On submit function
 * @param userInfo User info received form the user reducer
 * @param cookieCountryCode Country code from the cookie
 * @param isAuthenticated Whether the user is authenticated or not
 * @param disabledField Optional, Boolean value indicating whether form Fields are disabled
 * @param setUserCountry Optional, function to set user country and state in the parent component
 *
 * @returns Form render
 */

const ContactUsForm = (props: IContactUsFormProps) => {
  const intl = useIntl()

  let defaultValues: IContactUsForm = {
    name: '',
    email: '',
    phone: '',
    country: '',
    state: '',
    organization: '',
    comments: '',
    acceptTerms: false
  }

  const methods = useForm({ mode: 'all', defaultValues })

  const {
    handleSubmit,
    formState: { isSubmitting, isDirty },
    watch,
    reset,
    setValue
  } = methods

  const {
    disabledField,
    onSubmit,
    userInfo,
    cookieCountryCode,
    isAuthenticated,
    setUserCountry,
    recaptchaToken,
    handleTokenChange
  } = props

  const disabledByRecaptcha = !recaptchaToken && process.env.REACT_APP_is_test !== 'true'

  useEffect(() => {
    if (isAuthenticated && userInfo) {
      const userCountry: string =
        allCountries.find(([, code]) => code === userInfo?.BillToAccountObj?.Country)?.[0] || ''

      defaultValues = {
        ...defaultValues,
        name: userInfo.FirstName,
        email: userInfo.Email,
        phone: userInfo.BillToAccountObj.PhoneNumber,
        country: userCountry,
        state:
          userCountry === 'United States'
            ? US[2].find(([, code]) => code === userInfo?.BillToAccountObj?.State)?.[0] || ''
            : '',
        organization: userInfo.BillToAccountObj.CompanyName
      }
      reset(defaultValues)
    } else {
      const cookieUserCountry = allCountries.find(([, code]) => code === cookieCountryCode)?.[0] || ''

      defaultValues = {
        ...defaultValues,
        country: cookieUserCountry
      }
      reset(defaultValues)
    }
  }, [isAuthenticated])

  const values = watch()

  useEffect(() => {
    if (setUserCountry) {
      setUserCountry({ country: values.country, state: values.state || '' })
    }
  }, [values.country, values.state])

  return (
    <Paper
      data-gtm-click-subsection1='Contact Us Form'
      sx={{ maxWidth: 800, p: 4, px: { xs: 2, sm: 4 }, m: 'auto', width: '100%' }}
    >
      <FormProvider {...methods}>
        <form
          id='Contact Sales'
          style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}
          onSubmit={handleSubmit(onSubmit)}
        >
          <InputField
            readOnly={disabledField}
            name='name'
            title={intl.formatMessage({ id: 'contactUs.name' })}
            rules={{ ...validateRequired(intl) }}
          />
          <InputField
            readOnly={disabledField}
            name='email'
            title={intl.formatMessage({ id: 'account.email' })}
            rules={{ ...validateRequired(intl), validate: (value) => validateEmail(value, intl) }}
          />
          <InputField
            type='tel'
            name='phone'
            title={intl.formatMessage({ id: 'account.phone' })}
            rules={{ ...validateRequired(intl), validate: (value) => validatePhone(value, intl) }}
          />
          <SelectField
            name='country'
            options={allCountries}
            title={intl.formatMessage({ id: 'account.country' })}
            rules={{ ...validateRequired(intl) }}
            readOnly={disabledField}
            setValue={setValue}
          ></SelectField>
          {values.country === 'United States' && (
            <SelectField
              name='state'
              options={usStatesList}
              title={intl.formatMessage({ id: 'sales.selectState' })}
              rules={{ ...validateRequired(intl) }}
              setValue={setValue}
            ></SelectField>
          )}
          <InputField
            name='organization'
            title={intl.formatMessage({ id: 'account.company' })}
            rules={{ ...validateRequired(intl) }}
          />
          <InputField name='comments' title={intl.formatMessage({ id: 'contactUs.comment' })} multiline rows={5} />
          <SingleCheckbox
            name='acceptTerms'
            rules={{ ...validateRequired(intl) }}
            title={
              <FormattedMessage
                id='account.iHaveReadAndAccepted'
                values={{
                  privacyPolicy: (
                    <IntlLink to='/legal/privacy' target='_blank' rel='noopener noreferrer'>
                      <FormattedMessage id='account.privacyPolicy' />
                    </IntlLink>
                  )
                }}
              />
            }
          />
          <Recaptcha onChange={handleTokenChange} />
          <Button
            id='submit-button'
            data-gtm-click-subsection2='primary-cta'
            type='submit'
            disabled={isSubmitting || !isDirty || disabledByRecaptcha}
            variant='contained'
            color='primary'
            size='large'
            data-cy='contactus-send-email-button'
          >
            {isSubmitting ? <CircularProgress size={24} /> : <FormattedMessage id='contactUs.sendEmail' />}
          </Button>
        </form>
      </FormProvider>
    </Paper>
  )
}

/**
 * Select component
 * @param name param required by react-hook-forms to register and control the select
 * @param title label to be displayed in the select
 * @param rules rules to validate the form component
 * @param options list of options to be displayed in the select
 * @param readOnly to manage if it will be read only or not
 * @param setValue hook from the form to modify the value of the form when the country is changed
 *
 * @returns select component with options
 */

const SelectField = ({
  name,
  options,
  title,
  rules,
  readOnly,
  setValue
}: {
  name: string
  options: Array<[string, string, Array<[string, string]>?]>
  title: string
  rules?: RegisterOptions
  readOnly?: boolean
  setValue?: UseFormSetValue<IContactUsForm>
}) => {
  const { field, fieldState } = useController({ name, rules })

  const handleChange = (event: SelectChangeEvent<string>) => {
    if (event.target.name === 'country' && event.target.value !== 'United States') {
      setValue?.('state', '')
    }
    return event.target.value
  }

  return (
    <FormControl error={!!fieldState.error}>
      <InputLabel>{title}</InputLabel>
      <Select
        readOnly={readOnly}
        {...field}
        sx={{ textAlign: 'left', color: readOnly ? '#888' : undefined }}
        fullWidth
        label={title}
        data-cy={`contactus-${field.name}-dropdown`}
        onChange={(event) => field.onChange(handleChange(event))}
      >
        {options.map(([item], index) => {
          return (
            <MenuItem value={item} key={index}>
              {item}
            </MenuItem>
          )
        })}
      </Select>
      <FormHelperText>{fieldState.error?.message}</FormHelperText>
    </FormControl>
  )
}

/**
 * Checkbox component
 * @param name param required by react-hook-forms to register and control the select
 * @param title label to be displayed in the select
 * @param rules rules to validate the form component
 * @return component checkbox and the helper text component
 */

const SingleCheckbox = ({ name, title, rules }: { name: string; title: React.ReactNode; rules: RegisterOptions }) => {
  const { field, fieldState } = useController({ name, rules })

  return (
    <Box sx={{ textAlign: 'left' }}>
      <FormControlLabel
        control={
          <Checkbox
            checked={!!field.value}
            onChange={(event) => field.onChange(event.target.checked)}
            color='primary'
            data-cy={`contactus-${field.name}-checkbox`}
          />
        }
        label={title}
      />
      <FormHelperText error>{fieldState.error?.message}</FormHelperText>
    </Box>
  )
}

export default ContactUsForm
